/**
 * The dataset a type page picks cases for, as the URL names it: its name and type, and the scenarios
 * it already tests, which cannot be picked again. One that cannot be read ends the picking.
 */

import { useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';
import { simulationDatasetService } from '@/services/simulation-dataset.service';
import type { SimulationDataset } from '@/types/simulation-dataset.types';

export interface CasePicker {
	dataset: SimulationDataset;
	/** The scenarios its cases test. */
	inDataset: ReadonlySet<string>;
}

export const useCasePicker = (datasetId: string | null, onUnreadable: () => void): CasePicker | null => {
	const [picker, setPicker] = useState<CasePicker | null>(null);
	// Held by ref: the URL setter behind it changes with every URL change, which must not refetch the dataset.
	const onUnreadableRef = useRef(onUnreadable);
	useEffect(() => {
		onUnreadableRef.current = onUnreadable;
	}, [onUnreadable]);

	useEffect(() => {
		setPicker(null);
		if (!datasetId) return;
		let cancelled = false;
		simulationDatasetService.fetch(datasetId).then(
			({ dataset, cases }) => {
				if (!cancelled) setPicker({ dataset, inDataset: new Set(cases.map((datasetCase) => datasetCase.scenarioId)) });
			},
			(err) => {
				if (cancelled) return;
				toast.error(getApiErrorMessage(err, 'Could not load the dataset to add cases to'));
				onUnreadableRef.current();
			}
		);
		return () => {
			cancelled = true;
		};
	}, [datasetId]);

	return picker;
};
